import { CommonModule } from '@angular/common';
import { Component, Inject, LOCALE_ID, NgModule, OnDestroy, OnInit, ViewEncapsulation } from '@angular/core';
import { FormControl, FormGroup, ReactiveFormsModule } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatDialog } from '@angular/material/dialog';
import { FooterTemplate } from '@app/common/footer-template';
import { Roles } from '@app/common/roles';
import { HtmlEditorModule } from '@app/components/html-editor/html-editor.component';
import { SiteElement, SiteElementType } from '@app/models/site-element';
import { LinkModule } from '@app/directives/link.directive';
import { SafeModule } from '@app/pipes/safe.pipe';
import { AppSettingsService } from '@app/services/appsettings.service';
import { SecurityService } from '@app/services/security.service';
import { SiteElementsService } from '@app/services/site-elements.service';
import { forkJoin, Observable, of, Subject, Subscription } from 'rxjs';
import { concatMap, map, mergeMap, startWith } from 'rxjs/operators';
import { ConfirmDialogComponent, ConfirmDialogModule } from '../confirm-dialog/confirm-dialog.component';

@Component({
  selector: 'app-footer',
  templateUrl: './footer.component.html',
  styleUrls: ['./footer.component.scss'],
  encapsulation: ViewEncapsulation.None
})
export class FooterComponent implements OnInit, OnDestroy {
  siteElement$: Observable<SiteElement>;
  footerElementSubject$: Subject<boolean> = new Subject<boolean>();
  hasUserPermissionToEdit$: Observable<boolean>;
  existingSiteElement: SiteElement;
  editMode = false;
  isNew = false;
  subscription = new Subscription();
  footerEditorForm = new FormGroup({
    FooterContent: new FormControl(FooterTemplate.HTML),
  });
  availableLanguages: Array<string> = new Array<string>();
  defaultRequestCultureLanguage: string;

  constructor(@Inject(LOCALE_ID) private locale: string,
              public dialog: MatDialog,
              private siteElementsService: SiteElementsService,
              private securityService: SecurityService,
              private appSettingsService: AppSettingsService) { }

  ngOnInit() {
    this.siteElement$ = this.footerElementSubject$
      .pipe(
        startWith(true),
        mergeMap(() => {
          return forkJoin([
            this.siteElementsService.getSiteElements(SiteElementType.FOOTER),
            this.appSettingsService.getDefaultRequestCulture()
          ]);
        }),
        map(([siteElements, defaultRequestCulture]) => {
          this.defaultRequestCultureLanguage = defaultRequestCulture?.substr(0, 2);
          if (siteElements) {
            this.availableLanguages = siteElements.filter(s => s.Language).map(s => s.Language);

            // Sort the fetched site elements by priority
            const sortedSiteElements = siteElements.sort((a, b) => {

              // return -1 to sort a before b, return 1 to sort b before a

              if (a.Language === this.locale) {
                return -1;
              }
              if (b.Language === this.locale) {
                return 1;
              }

              if (a.Language === this.defaultRequestCultureLanguage) {
                return -1;
              }
              if (b.Language === this.defaultRequestCultureLanguage) {
                return 1;
              }

              // legacy footer (put last)
              if (a.Language === null) {
                return 1;
              }
              if (b.Language === null) {
                return -1;
              }

              return 1;
            });

            const siteElement = sortedSiteElements[0];
            if (siteElement) {
              this.footerEditorForm.setValue({ FooterContent: siteElement.Content });
              this.existingSiteElement = siteElement;

              if (siteElement.Language !== this.locale) {
                // If the selected footer doesn't match the current language, it means a footer for the current
                // language doesn't exist (it could, for example, be the fallback footer); force creation of new footer
                this.isNew = true;
              } else {
                this.isNew = false;
              }

              return siteElement;
            } else {
              this.isNew = true;
              return new SiteElement({ Content: FooterTemplate.HTML });
            }
          } else {
            this.isNew = true;
            return new SiteElement({ Content: FooterTemplate.HTML });
          }
        }));

    this.hasUserPermissionToEdit$ = this.securityService.hasUserAnyAppRole([Roles.Admin, Roles.FooterEditor]);
  }

  editFooter() {
    this.editMode = true;
  }

  cancel() {
    this.editMode = false;
  }

  getTemplate() {
    this.footerEditorForm.setValue({ FooterContent: FooterTemplate.HTML });
  }

  getAvailableLanguages(languagesVar: Array<HTMLInputElement>, andVar: HTMLInputElement, defaultVar: HTMLInputElement) {
    const languages: any[] = languagesVar.map(l => ({ language: l.innerText, tag: l.className })).filter(l => this.availableLanguages.some(cl => cl === l.tag));

    const sortedLanguages = languages.sort((a, b) => {
      if (this.defaultRequestCultureLanguage && a.tag === this.defaultRequestCultureLanguage) {
        return -1;
      } else {
        return a.language.localeCompare(b.language);
      }
    });

    let availableLanguagesOutput = '';
    for (let i = 0; i < sortedLanguages.length; i++) {
      const currentLanguage = (sortedLanguages[i].tag === this.defaultRequestCultureLanguage ? `${sortedLanguages[i].language} (${defaultVar.innerText})` : sortedLanguages[i].language);
      if (sortedLanguages.length === 1) {
        // Special case: only one language
        availableLanguagesOutput += `${currentLanguage}.`;
      } else if (i < sortedLanguages.length - 2) {
        // At least two more elements after this one
        availableLanguagesOutput += `${currentLanguage}, `;
      } else if (i < sortedLanguages.length - 1) {
        // One more element after this one
        availableLanguagesOutput += `${currentLanguage} `;
      } else {
        // Last element
        availableLanguagesOutput += `${andVar.innerText} ${currentLanguage}.`;
      }
    }
    return availableLanguagesOutput;
  }

  save() {
    const siteElementToAddOrUpdate = new SiteElement();
    siteElementToAddOrUpdate.Content = this.footerEditorForm.get('FooterContent').value;
    siteElementToAddOrUpdate.Language = this.locale;
    siteElementToAddOrUpdate.Type = SiteElementType.FOOTER;
    if (this.isNew) {
      // add a new footer setting
      this.subscription.add(
        this.siteElementsService.addSiteElement(siteElementToAddOrUpdate)
          .subscribe(() => {
            this.editMode = false;
            this.footerElementSubject$.next(true);
          }));
    } else {
      // open dialog
      const dialogRef = this.dialog.open(ConfirmDialogComponent);
      siteElementToAddOrUpdate.Id = this.existingSiteElement.Id;
      // wait for user to confirm before saving changes
      this.subscription.add(
        dialogRef.afterClosed()
          .pipe(concatMap(result => {
            if (result) {
              // update existing footer setting
              return this.siteElementsService.updateSiteElements(siteElementToAddOrUpdate);
            } else {
              return of(null);
            }
          }))
          .subscribe(() => {
            this.editMode = false;
            this.footerElementSubject$.next(true);
          }));
    }
  }

  ngOnDestroy(): void {
    this.subscription && this.subscription.unsubscribe();
  }
}
@NgModule({
  declarations: [FooterComponent],
  exports: [FooterComponent],
  imports: [
    CommonModule,
    ReactiveFormsModule,
    HtmlEditorModule,
    MatButtonModule,
    ConfirmDialogModule,
    LinkModule,
    SafeModule,
  ]
})
export class FooterModule { }
